import { useParams } from "react-router-dom";
import { Box, ListItemText, useMediaQuery, useTheme, Link } from "@mui/material";
import {
    NavigateNext as NavigateNextIcon,
    Delete as DeleteIcon
} from '@mui/icons-material';
import { pink, red } from "@mui/material/colors";
import { muiGreen, muiAmber, prodBase } from "@initia-digital/colors";

import { v4 as uuidv4 } from 'uuid';

import { useMSALPayload } from "@initia-digital/auth-client-utils";
import {
    CheckboxListItemButton,
    ExpandableFileListItem,
    FileListItem,
    FilledButton, GeneralChip, IconButton,
    Typography, useSnackbar,
    UploadFileButton
} from "@initia-digital/component-library";
import { baseDefinedAndNotNullNarrowing, S3 } from "@initia-digital/ts-utils";
import { IFileUpload, FileUploadStatus } from "@initia-digital/types/work-and-safety";

import { useWorkAndSafetyStore } from "../store";
import { putS3Object, getS3Object } from "../api/objectStorage";
import { PlanActions } from "./PlanActions";
import { IPlanStageProps } from "../interfaces/componentInterfaces";
import { TeamMembersMultiselectAutoComplete } from "./Autosuggest";
import { SectionHeader } from "./SectionHeader";
import { PlanHeader } from "./PlanHeader";

// Standard filenames for service clearance uploads
const STANDARD_FILENAMES = {
    GROUND_CLEARANCE_CHECKLIST: "Ground Clearance Checklist",
    INVESTIGATION_PLAN: "Site Investigation Plan"
};

export const PlanServices = (props: IPlanStageProps) => {
    // -------------------------------
    const {
        handleSave, handleSaveAndNext,
        handleStatusChange
    } = props;
    const { editPlanState, updateEditPlanState } = useWorkAndSafetyStore();
    const {
        title, status,
        serviceClearanceRequired, bookings,
        fileUploads,
    } = editPlanState;


    // Check for key SOP components
    const hasGroundClearanceChecklist = fileUploads.some(file =>
        file.name.includes(STANDARD_FILENAMES.GROUND_CLEARANCE_CHECKLIST) && file.includeInAppendix
    );
    const hasInvestigationPlan = fileUploads.some(file =>
        file.name.includes(STANDARD_FILENAMES.INVESTIGATION_PLAN) && file.includeInAppendix
    );
    const serviceClearanceBooked = bookings.rows.filter(booking => booking.bookingType === 'Service clearance').length > 0;

    const params = useParams();
    const { planId, jobId } = params;

    const theme = useTheme();
    const isSmallerThanSm = useMediaQuery(theme.breakpoints.down('sm'));

    const { queueSnackbar } = useSnackbar();

    const authDetail = useMSALPayload();

    // File upload handler for service clearance files
    const handleServiceClearanceFileUpload = async (files: File[], standardFileName: string) => {
        if (!baseDefinedAndNotNullNarrowing(authDetail) || files.length === 0) {
            return;
        }

        try {
            const file = files[0]; // Single file upload

            // Validate PDF only
            if (!file.name.toLowerCase().endsWith('.pdf')) {
                queueSnackbar('Only PDF files are allowed', 'error', 'Invalid File Type', 3000);
                return;
            }

            const fileId = uuidv4();
            const path = `${import.meta.env.VITE_ENV_LOWER}-work-and-safety/${jobId}/plan/file-upload-${fileId}/original/${S3.toSafePath(standardFileName + '.pdf')}`;

            // Remove existing file with same standard name
            const updatedFileUploads = editPlanState.fileUploads.filter(
                existingFile => !existingFile.name.includes(standardFileName)
            );

            // Upload to S3 first
            await putS3Object(authDetail, {
                file,
                path
            });

            // Create new file upload object (matching SitePlansAndPermits structure)
            const newFileUpload: IFileUpload = {
                id: fileId,
                path: path, // Use path instead of s3Key
                name: standardFileName + '.pdf', // Include .pdf extension for consistency
                notes: '',
                size: file.size,
                jobId: jobId ?? '',
                planId: planId ?? '',
                uploadStatus: 'PENDING' as FileUploadStatus,
                includeInAppendix: true // Always append these files
            };

            // Update state with new file
            const updateState = {
                ...editPlanState,
                fileUploads: [...updatedFileUploads, newFileUpload]
            }
            updateEditPlanState(updateState);

            // Save the plan to persist the file upload
            await handleSave(updateState);

            queueSnackbar(`${standardFileName} uploaded successfully`, 'success', 'Upload Complete', 3000);

        } catch (error) {
            console.error('File upload error:', error);
            queueSnackbar('Failed to upload file', 'error', 'Upload Error', 3000);
        }
    };

    // File removal handler
    const handleFileRemove = async (standardFileName: string) => {
        try {
            // Remove file from state
            const updatedFileUploads = editPlanState.fileUploads.filter(
                existingFile => !existingFile.name.includes(standardFileName)
            );

            const updateState = {
                ...editPlanState,
                fileUploads: updatedFileUploads
            }
            updateEditPlanState(updateState);

            // Save the plan to persist the file removal
            await handleSave(updateState);

            queueSnackbar(`${standardFileName} removed successfully`, 'success', 'File Removed', 3000);

        } catch (error) {
            console.error('File removal error:', error);
            queueSnackbar('Failed to remove file', 'error', 'Removal Error', 3000);
        }
    };

    // File preview loader
    const loadFileUrl = async (fileUpload: IFileUpload) => {
        const { id, path } = fileUpload;
        if (!baseDefinedAndNotNullNarrowing(path) || !baseDefinedAndNotNullNarrowing(id)) {
            throw new Error('File upload is missing id or path');
        }

        const response = await getS3Object(authDetail, { path });
        if (response?.buffer) {
            try {
                const blob = new Blob([response.buffer], { type: response.mimeType ?? 'application/pdf' });
                const url = URL.createObjectURL(blob);
                return url;
            } catch (error) {
                console.error('Error creating object URL:', error);
                queueSnackbar(`Error processing ${fileUpload.name}`, 'error', 'Error', 3000);
                throw error;
            }
        }
        throw new Error('File upload is missing buffer');
    };

    // Scroll to bookings section with 50px offset
    const scrollToBookings = () => {
        const bookingsSection = document.querySelector('#bookings-section');
        if (bookingsSection) {
            const rect = bookingsSection.getBoundingClientRect();
            const elementPosition = rect.top + window.pageYOffset;
            const offsetPosition = elementPosition - 100;

            window.scrollTo({
                top: offsetPosition,
                behavior: 'smooth'
            });
        }
    };


    return (
        <Box sx={{ width: '100%' }}>
            <PlanHeader
                id={planId}
                title={title}
                status={status}
            />
            <Box
                display="flex" flexDirection={isSmallerThanSm ? "column" : "row"} flexWrap="wrap"
                alignItems="center" justifyContent="space-between"
                maxWidth="100%"
            >
                <SectionHeader label="Services" />
                <PlanActions
                    handleSave={handleSave}
                    handleStatusChange={handleStatusChange}
                />
            </Box>

            <Box display="flex" flexDirection="column" mt="10px" mb="30px">
                <Box>
                    <Typography
                        fontSize={"0.9rem"}
                        fontWeight={700}
                        color={theme.palette.primary.contrastText}
                    >
                        Underground or Overhead Services
                    </Typography>
                </Box>
                <Box display="flex" flexDirection="row" alignItems="center" gap={1}>
                    <CheckboxListItemButton
                        isSelected={serviceClearanceRequired}
                        onSelect={() => {
                            updateEditPlanState({
                                ...editPlanState,
                                serviceClearanceRequired: !serviceClearanceRequired
                            });
                        }}
                        label={
                            <ListItemText>
                                <Box display="flex" flexDirection="column" gap="4px">
                                    <Typography
                                        fontSize={"0.75rem"}
                                        fontWeight={300}
                                        color={theme.palette.primary.contrastText}
                                    >
                                        Is there potential for activities to impact underground or overhead services?
                                    </Typography>
                                    <Typography
                                        fontSize={"0.6rem"}
                                        fontWeight={300}
                                        color={theme.palette.primary.contrastText}
                                    >
                                        e.g. machine drilling, coring, test pitting, CPTs, hand augering, scala penetrometer, or general excavation
                                    </Typography>
                                </Box>
                            </ListItemText>
                        }
                    />
                </Box>

                {serviceClearanceRequired && (
                    <Box display="flex" flexDirection="column" gap="16px" ml="20px">
                        {/* SOP Reference - Moved to top */}
                        <Box
                            display="flex"
                            flexDirection="column"
                            gap="4px"
                            border={`1px solid ${prodBase.secondary.main}`}
                            borderRadius="6px"
                            py="8px"
                            px="12px"
                        >
                            <Typography
                                fontSize="0.7rem"
                                color={prodBase.secondary.main}
                                fontWeight={600}
                            >
                                Initia's Service Clearance Standard Operating Procedure (SOP) applies
                            </Typography>
                            <FileListItem
                                filename="Initia Service Clearance SOP Rev5.pdf"
                                selected={false}
                                onClick={async () => {
                                    try {
                                        const response = await fetch('/Initia Service Clearance SOP Rev5.pdf');
                                        if (!response.ok) {
                                            throw new Error('Failed to load SOP PDF');
                                        }
                                        const blob = await response.blob();
                                        const url = window.URL.createObjectURL(blob);
                                        const link = document.createElement('a');
                                        link.href = url;
                                        link.download = 'Initia Service Clearance SOP Rev5.pdf';
                                        document.body.appendChild(link);
                                        link.click();
                                        document.body.removeChild(link);
                                        window.URL.revokeObjectURL(url);
                                    } catch (error) {
                                        console.error('Error downloading SOP PDF:', error);
                                        queueSnackbar('Error downloading Service Clearance SOP', 'error', 'Error', 3000);
                                    }
                                }}
                            />
                            <Typography
                                fontSize="0.6rem"
                                color={theme.palette.primary.contrastText}
                                fontWeight={300}
                            >
                                The steps below are designed to help you follow the SOP
                            </Typography>
                        </Box>

                        {/* Step 1: Site Investigation Lead Selection */}
                        <Box
                            display="flex"
                            flexDirection="column"
                            gap="4px"
                            border={`2px solid ${editPlanState.ourContact
                                ? muiGreen[200]
                                : editPlanState.teamMembers.length === 0
                                    ? red[200]
                                    : red[200]
                                }`}
                            borderRadius="8px"
                            p="16px"
                        >
                            <Typography
                                fontSize="0.8rem"
                                fontWeight={600}
                                color={theme.palette.primary.contrastText}
                            >
                                {editPlanState.ourContact ? '✅' : '❌'} Step 1: Site Investigation Lead
                            </Typography>
                            {/* Team Member Selection */}
                            <Box display="flex" flexDirection="column">
                                <Typography fontSize="0.8rem" fontWeight={300} color={theme.palette.primary.contrastText}>
                                    Initia Team Members
                                </Typography>
                                <TeamMembersMultiselectAutoComplete
                                    name="teamMembers"
                                    handleAutoSuggestSelectStateChange={(event) => {
                                        if (Array.isArray(event.target.value) && event.target.value.length === 0) {
                                            updateEditPlanState({
                                                ...editPlanState,
                                                [event.target.name]: event.target.value,
                                                ourContact: null
                                            });
                                        } else {
                                            updateEditPlanState({
                                                ...editPlanState,
                                                [event.target.name]: event.target.value
                                            });
                                        }
                                    }}
                                    selectedValues={editPlanState.teamMembers}
                                />
                            </Box>

                            {/* Lead Selection */}
                            <Box display="flex" flexDirection="column">
                                <Box>
                                    <Typography
                                        fontSize={"0.8rem"}
                                        fontWeight={300}
                                        color={theme.palette.primary.contrastText}
                                    >
                                        {editPlanState.serviceClearanceRequired ? "Site Investigation Lead" : "Initia Lead"}
                                    </Typography>
                                </Box>
                                <Typography
                                    color={theme.palette.primary.contrastText}
                                    fontWeight={300}
                                    fontSize="0.65rem"
                                >
                                    {editPlanState.serviceClearanceRequired
                                        ? "Select the person responsible for site investigation per Initia's Service Clearance SOP"
                                        : "Select the Initia lead for this project"
                                    }
                                </Typography>
                                <Box
                                    display="flex"
                                    flexDirection="row"
                                    flexWrap="wrap"
                                    gap="2px"
                                >
                                    {/* Team member options */}
                                    {editPlanState.teamMembers
                                        .map((member) => (
                                            <GeneralChip
                                                key={`contact-${member}`}
                                                label={member}
                                                backgroundColor={editPlanState.ourContact === member ? pink[500] : theme.palette.primary.main}
                                                color={editPlanState.ourContact === member ? theme.palette.common.white : theme.palette.primary.contrastText}
                                                size="small"
                                                clickable
                                                handleClick={() => {
                                                    updateEditPlanState({
                                                        ...editPlanState,
                                                        ourContact: editPlanState.ourContact === member ? null : member
                                                    });
                                                }}
                                            />
                                        ))}
                                </Box>
                            </Box>

                            {!editPlanState.ourContact && (
                                <Box>
                                    <Typography
                                        fontSize="0.65rem"
                                        color={red[700]}
                                        fontWeight={500}
                                    >
                                        {editPlanState.teamMembers.length === 0
                                            ? "Please select Initia team members first, then select a Site Investigation Lead"
                                            : "Site investigation lead is required - please select a team member"
                                        }
                                    </Typography>
                                </Box>
                            )}
                            {editPlanState.ourContact && (
                                <Box>
                                    <Typography
                                        fontSize="0.7rem"
                                        color={muiGreen[600]}
                                        fontWeight={500}
                                    >
                                        Site investigation lead selected
                                    </Typography>
                                </Box>
                            )}
                        </Box>

                        <Box
                            display="flex"
                            flexDirection="column"
                            gap="4px"
                            border={`2px solid ${hasGroundClearanceChecklist ? muiGreen[200] : muiAmber[200]}`}
                            borderRadius="8px"
                            p="16px"
                        >
                            <Typography
                                fontSize="0.8rem"
                                fontWeight={600}
                                color={theme.palette.primary.contrastText}
                            >
                                {hasGroundClearanceChecklist ? '✅' : '⚠️'} Step 2: Ground Clearance Checklist
                            </Typography>
                            <Typography
                                fontSize="0.7rem"
                                color={theme.palette.text.secondary}
                            >
                                Download, complete and upload the ground clearance checklist
                            </Typography>
                            {!hasGroundClearanceChecklist && (
                                <Typography
                                    fontSize="0.65rem"
                                    color={muiAmber[700]}
                                    fontWeight={500}
                                >
                                    Director approval required if checklist not uploaded
                                </Typography>
                            )}

                            <Box display="flex" flexDirection="column" gap="12px">
                                <Box display="flex" gap="12px" alignItems="center">
                                    <FileListItem
                                        filename="Initia Ground Clearance Checklist Rev 5.pdf"
                                        selected={false}
                                        onClick={async () => {
                                            try {
                                                const response = await fetch('/Initia Ground Clearance Checklist Rev 5.pdf');
                                                if (!response.ok) {
                                                    throw new Error('Failed to load checklist PDF');
                                                }
                                                const blob = await response.blob();
                                                const url = window.URL.createObjectURL(blob);
                                                const link = document.createElement('a');
                                                link.href = url;
                                                link.download = 'Initia Ground Clearance Checklist Rev 5.pdf';
                                                document.body.appendChild(link);
                                                link.click();
                                                document.body.removeChild(link);
                                                window.URL.revokeObjectURL(url);
                                            } catch (error) {
                                                console.error('Error downloading checklist PDF:', error);
                                                queueSnackbar('Error downloading Ground Clearance Checklist', 'error', 'Error', 3000);
                                            }
                                        }}
                                    />
                                </Box>

                                <UploadFileButton
                                    accept={{
                                        'application/pdf': ['.pdf']
                                    }}
                                    maxFiles={1}
                                    handleFileSelection={(files) => handleServiceClearanceFileUpload(files, STANDARD_FILENAMES.GROUND_CLEARANCE_CHECKLIST)}
                                />

                                {hasGroundClearanceChecklist && (
                                    <>
                                        <Typography
                                            fontSize="0.7rem"
                                            color={muiGreen[600]}
                                            fontWeight={500}
                                        >
                                            Completed checklist uploaded
                                        </Typography>
                                        {/* Display uploaded checklist file */}
                                        {(() => {
                                            const uploadedFile = fileUploads.find(file =>
                                                file.name.includes(STANDARD_FILENAMES.GROUND_CLEARANCE_CHECKLIST) && file.includeInAppendix
                                            );
                                            if (uploadedFile) {
                                                return (
                                                    <Box border={`2px solid ${muiGreen[200]}`} borderRadius="4px">
                                                        <ExpandableFileListItem
                                                            loadUrl={() => loadFileUrl(uploadedFile)}
                                                            filename={uploadedFile.name}
                                                            fileSize={uploadedFile.size}
                                                            actionButtons={[
                                                                <IconButton
                                                                    key="delete"
                                                                    size="small"
                                                                    tooltipTitle="Remove completed checklist"
                                                                    onClick={() => handleFileRemove(STANDARD_FILENAMES.GROUND_CLEARANCE_CHECKLIST)}
                                                                >
                                                                    <DeleteIcon />
                                                                </IconButton>
                                                            ]}
                                                            showMetadataOnExpand={false}
                                                        />
                                                    </Box>
                                                );
                                            }
                                            return null;
                                        })()}
                                    </>
                                )}
                            </Box>
                        </Box>

                        {/* Optional: Investigation Planner */}
                        <Box
                            display="flex"
                            flexDirection="column"
                            gap="4px"
                            border={`1px solid ${theme.palette.divider}`}
                            borderRadius="8px"
                            p="16px"
                            bgcolor={theme.palette.background.paper}
                        >
                            <Typography
                                fontSize="0.8rem"
                                fontWeight={600}
                                color={theme.palette.primary.contrastText}
                            >
                                (Optional): BETA Investigation Planner
                            </Typography>
                            <Typography
                                fontSize="0.7rem"
                                color={theme.palette.secondary.main}
                            >
                                This is a BETA release
                            </Typography>
                            <Typography
                                fontSize="0.7rem"
                                color={theme.palette.secondary.main}
                            >
                                You can use this tool to place investigation locations with reference to Koordinates service data and then export CSV coordinates you can use to prepare a CAD investigation plan
                            </Typography>

                            <Box
                                display="flex"
                            >
                                <FilledButton
                                    size="small"
                                    onClick={() => window.open('https://concept.initia-digital.co.nz/investigation_planner/index.html', '_blank')}
                                >
                                    Investigation Planner (BETA)
                                </FilledButton>
                            </Box>
                        </Box>

                        <Box
                            display="flex"
                            flexDirection="column"
                            gap="4px"
                            border={`2px solid ${hasInvestigationPlan ? muiGreen[200] : muiAmber[200]}`}
                            borderRadius="8px"
                            p="16px"
                        >
                            <Typography
                                fontSize="0.8rem"
                                fontWeight={600}
                                color={theme.palette.primary.contrastText}
                            >
                                {hasInvestigationPlan ? '✅' : '⚠️'} Step 3: Site Investigation Plan
                            </Typography>
                            <Typography
                                fontSize="0.7rem"
                                color={theme.palette.text.secondary}
                            >
                                Upload the investigation plan from your CAD team
                            </Typography>
                            {!hasInvestigationPlan && (
                                <Typography
                                    fontSize="0.65rem"
                                    color={muiAmber[700]}
                                    fontWeight={500}
                                >
                                    Director approval required if investigation plan not uploaded
                                </Typography>
                            )}

                            <UploadFileButton
                                accept={{
                                    'application/pdf': ['.pdf']
                                }}
                                maxFiles={1}
                                handleFileSelection={(files) => handleServiceClearanceFileUpload(files, STANDARD_FILENAMES.INVESTIGATION_PLAN)}
                            />

                            {hasInvestigationPlan && (
                                <Box
                                    mt="8px"
                                >
                                    {/* Display uploaded investigation plan file */}
                                    {(() => {
                                        const uploadedFile = fileUploads.find(file =>
                                            file.name.includes(STANDARD_FILENAMES.INVESTIGATION_PLAN) && file.includeInAppendix
                                        );
                                        if (uploadedFile) {
                                            return (
                                                <Box borderRadius="4px">
                                                    <ExpandableFileListItem
                                                        loadUrl={() => loadFileUrl(uploadedFile)}
                                                        filename={uploadedFile.name}
                                                        fileSize={uploadedFile.size}
                                                        actionButtons={[
                                                            <IconButton
                                                                key="delete"
                                                                size="small"
                                                                tooltipTitle="Remove investigation plan"
                                                                onClick={() => handleFileRemove(STANDARD_FILENAMES.INVESTIGATION_PLAN)}
                                                            >
                                                                <DeleteIcon />
                                                            </IconButton>
                                                        ]}
                                                        showMetadataOnExpand={false}
                                                    />
                                                </Box>
                                            );
                                        }
                                        return null;
                                    })()}
                                </Box>
                            )}
                        </Box>

                        <Box
                            display="flex"
                            flexDirection="column"
                            gap="4px"
                            border={`2px solid ${serviceClearanceBooked ? muiGreen[200] : muiAmber[200]}`}
                            borderRadius="8px"
                            p="16px"
                        >
                            <Typography
                                fontSize="0.8rem"
                                fontWeight={600}
                                color={theme.palette.primary.contrastText}
                            >
                                {serviceClearanceBooked ? '✅' : '⚠️'} Step 4: Service Clearance Booking
                            </Typography>
                            <Typography
                                fontSize="0.7rem"
                                color={theme.palette.text.secondary}
                            >
                                Book service clearance in the{' '}
                                <Link
                                    component="button"
                                    onClick={scrollToBookings}
                                    sx={{
                                        color: theme.palette.secondary.main,
                                        fontSize: '0.7rem',
                                        fontWeight: 500,
                                        cursor: 'pointer',
                                        textDecoration: 'underline'
                                    }}
                                >
                                    bookings section
                                </Link>
                                {' '}below
                            </Typography>

                            <Typography
                                fontSize="0.75rem"
                                color={serviceClearanceBooked ? muiGreen[700] : muiAmber[700]}
                                fontWeight={500}
                            >
                                {serviceClearanceBooked
                                    ? "Service clearance has been booked"
                                    : "Director approval required if service clearance not booked"
                                }
                            </Typography>
                        </Box>
                    </Box>
                )}
            </Box>


            <Box
                width="100%"
                display="flex" flexDirection="row" justifyContent="flex-end"
                pt="12px"
            >
                <FilledButton
                    onClick={handleSaveAndNext}
                    endIcon={<NavigateNextIcon />}
                >
                    Save and Next
                </FilledButton>
            </Box>
        </Box>
    )
}
